import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import AdminBookCard from './AdminBookCard';

const emptyBook = {
  id: '',
  name: '',
  author: '',
  genre: '',
  parts: '',
  publish_year: '',
  uploaded_date: '',
  youtube_links: [],
  rumble_links: [],
  featured: false,
  cover_image_url: ''
};

const resizeLinkArrays = (count, youtubeLinks = [], rumbleLinks = []) => {
  const n = Math.max(0, Number(count) || 0);
  return {
    youtube_links: Array.from({ length: n }, (_, i) => youtubeLinks[i] || ''),
    rumble_links: Array.from({ length: n }, (_, i) => rumbleLinks[i] || '')
  };
};

const getBookLinkArrays = (book) => {
  let youtubeLinks = book.youtube_links;
  let rumbleLinks = book.rumble_links;
  if (!Array.isArray(youtubeLinks) || youtubeLinks.length === 0) {
    youtubeLinks = book.youtube_link ? [book.youtube_link] : [];
  }
  if (!Array.isArray(rumbleLinks) || rumbleLinks.length === 0) {
    rumbleLinks = book.rumble_link ? [book.rumble_link] : [];
  }
  return { youtubeLinks, rumbleLinks };
};

function AdminDashboard() {
  // .env.production sets REACT_APP_API_URL for deploy. Dev defaults to localhost.
  const API_URL = useMemo(() => process.env.REACT_APP_API_URL ?? (process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : ''), []);
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState(emptyBook);
  const [saving, setSaving] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage] = useState(6);

  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_URL}/api/books`);
      if (!response.ok) {
        throw new Error('Failed to load books');
      }
      const data = await response.json();
      setBooks(data.books || []);
    } catch (err) {
      setError(err.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    const isAuthed = localStorage.getItem('adminAuthenticated') === 'true';
    if (!isAuthed) {
      navigate('/admin/login', { replace: true });
      return;
    }
    fetchBooks();
  }, [fetchBooks, navigate]);

  useEffect(() => {
    const filtered = books.filter((book) => {
      const bookName = book.name || '';
      const bookAuthor = book.author || '';
      const term = searchTerm.toLowerCase();
      return (
        bookName.toLowerCase().includes(term) ||
        bookAuthor.toLowerCase().includes(term)
      );
    });
    setFilteredBooks(filtered);
    setCurrentPage(1);
  }, [books, searchTerm]);

  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (currentPage <= 3) {
        endPage = maxPagesToShow;
      } else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }

    return pageNumbers;
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminAuthenticated');
    localStorage.removeItem('adminLoginTime');
    navigate('/admin/login', { replace: true });
  };

  const openCreateForm = () => {
    setEditingBook(null);
    setFormData(emptyBook);
    setCoverImageFile(null);
    setImagePreview(null);
    setShowForm(true);
  };

  const openEditForm = (book) => {
    const { youtubeLinks, rumbleLinks } = getBookLinkArrays(book);
    const partsValue = book.parts ?? '';
    const links = resizeLinkArrays(partsValue, youtubeLinks, rumbleLinks);
    setEditingBook(book);
    setFormData({
      id: book.id || '',
      name: book.name || '',
      author: book.author || '',
      genre: book.genre || '',
      parts: partsValue,
      publish_year: book.publish_year ?? '',
      uploaded_date: book.uploaded_date || '',
      youtube_links: links.youtube_links,
      rumble_links: links.rumble_links,
      featured: Boolean(book.featured),
      cover_image_url: book.cover_image_url || ''
    });
    setCoverImageFile(null);
    setImagePreview(book.cover_image_url || null);
    setShowForm(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'parts') {
      setFormData((prev) => ({
        ...prev,
        parts: value,
        ...resizeLinkArrays(value, prev.youtube_links, prev.rumble_links)
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePartLinkChange = (platform, index, value) => {
    const key = platform === 'youtube' ? 'youtube_links' : 'rumble_links';
    setFormData((prev) => {
      const updated = [...(prev[key] || [])];
      updated[index] = value;
      return { ...prev, [key]: updated };
    });
  };

  const handleFeaturedChange = (event) => {
    setFormData((prev) => ({ ...prev, featured: event.target.checked }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCoverImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData(emptyBook);
    setEditingBook(null);
    setCoverImageFile(null);
    setImagePreview(null);
  };

  const normalizePayload = (payload) => ({
    ...payload,
    parts: payload.parts === '' ? null : Number(payload.parts),
    publish_year: payload.publish_year === '' ? null : Number(payload.publish_year)
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const method = editingBook ? 'PUT' : 'POST';
      const endpoint = editingBook
        ? `${API_URL}/api/books/${editingBook.id}`
        : `${API_URL}/api/books`;

      // Use FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('author', formData.author);
      if (formData.genre) formDataToSend.append('genre', formData.genre);
      if (formData.parts !== '') formDataToSend.append('parts', formData.parts);
      if (formData.publish_year !== '') formDataToSend.append('publish_year', formData.publish_year);
      if (formData.uploaded_date) formDataToSend.append('uploaded_date', formData.uploaded_date);
      formDataToSend.append('youtube_links', JSON.stringify(formData.youtube_links || []));
      formDataToSend.append('rumble_links', JSON.stringify(formData.rumble_links || []));
      formDataToSend.append('featured', formData.featured ? 'true' : 'false');
      
      // Add cover image if selected
      if (coverImageFile) {
        formDataToSend.append('cover_image', coverImageFile);
      }

      const response = await fetch(endpoint, {
        method,
        body: formDataToSend
        // Don't set Content-Type header - browser will set it with boundary for FormData
      });

      if (!response.ok) {
        let message = 'Failed to save book';
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          message = data.detail || data.error || message;
        } catch (_) {
          if (text) message = text;
        }
        throw new Error(message);
      }

      await fetchBooks();
      closeForm();
    } catch (err) {
      setError(err.message || 'Failed to save book');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm('Delete this book?')) {
      return;
    }
    try {
      setError('');
      const response = await fetch(`${API_URL}/api/books/${bookId}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || 'Failed to delete book');
      }
      await fetchBooks();
    } catch (err) {
      setError(err.message || 'Failed to delete book');
    }
  };

  const currentFeatured = books.find((book) => book.featured);
  const canSelectFeatured =
    !currentFeatured || (editingBook && currentFeatured.id === editingBook.id);

  return (
    <div className="admin-dashboard-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <button
          type="button"
          className="logout-button"
          onClick={handleLogout}
        >
          Log out
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="admin-actions">
        <button type="button" className="add-button" onClick={openCreateForm}>
          + Add New Book
        </button>
      </div>

      <div className="admin-search-section">
        <div className="admin-search-container">
          <input
            type="text"
            placeholder="Search by book name or author..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              className="admin-clear-button"
              onClick={() => setSearchTerm('')}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading books...</div>
      ) : (
        <div className="books-list">
          <h2>Existing Books</h2>
          {filteredBooks.length > 0 && (
            <div className="admin-results-info">
              Showing {indexOfFirstBook + 1}-{Math.min(indexOfLastBook, filteredBooks.length)} of {filteredBooks.length} books
            </div>
          )}
          {filteredBooks.length === 0 ? (
            <p>
              {books.length === 0
                ? 'No books found. Add your first audiobook.'
                : 'No books match your search.'}
            </p>
          ) : (
            <>
              <div className="books-grid">
                {currentBooks.map((book) => (
                  <AdminBookCard
                    key={book.id}
                    book={book}
                    onEdit={openEditForm}
                    onDelete={handleDelete}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="admin-pagination">
                  <button
                    type="button"
                    className="admin-pagination-button"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>

                  <div className="admin-pagination-numbers">
                    {currentPage > 3 && totalPages > 5 && (
                      <>
                        <button
                          type="button"
                          className="admin-pagination-number"
                          onClick={() => handlePageChange(1)}
                        >
                          1
                        </button>
                        <span className="admin-pagination-ellipsis">...</span>
                      </>
                    )}

                    {getPageNumbers().map((number) => (
                      <button
                        key={number}
                        type="button"
                        className={`admin-pagination-number ${currentPage === number ? 'active' : ''}`}
                        onClick={() => handlePageChange(number)}
                      >
                        {number}
                      </button>
                    ))}

                    {currentPage < totalPages - 2 && totalPages > 5 && (
                      <>
                        <span className="admin-pagination-ellipsis">...</span>
                        <button
                          type="button"
                          className="admin-pagination-number"
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    className="admin-pagination-button"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {showForm && (
        <div className="book-form-overlay">
          <div className="book-form">
            <h2>{editingBook ? 'Edit Book' : 'Add Book'}</h2>
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Book Title</label>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="author">Author</label>
                  <input
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="genre">Genre(s)</label>
                  <input
                    id="genre"
                    name="genre"
                    value={formData.genre}
                    onChange={handleInputChange}
                    placeholder="Mystery, Thriller"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="parts">Parts</label>
                  <input
                    id="parts"
                    name="parts"
                    type="number"
                    min="0"
                    value={formData.parts}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="publish_year">Publish Year</label>
                  <input
                    id="publish_year"
                    name="publish_year"
                    type="number"
                    min="0"
                    value={formData.publish_year}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="uploaded_date">Uploaded Date</label>
                  <input
                    id="uploaded_date"
                    name="uploaded_date"
                    type="date"
                    value={formData.uploaded_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {Number(formData.parts) > 0 && (
                <div className="part-links-section">
                  <h3>Part Video Links</h3>
                  <p className="part-links-hint">
                    Add a YouTube and/or Rumble URL for each part.
                  </p>
                  {(formData.youtube_links || []).map((_, index) => (
                    <div key={`part-${index}`} className="part-link-row">
                      <div className="part-link-label">Part {index + 1}</div>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor={`youtube_link_${index}`}>YouTube Link</label>
                          <input
                            id={`youtube_link_${index}`}
                            type="url"
                            value={formData.youtube_links[index] || ''}
                            onChange={(e) => handlePartLinkChange('youtube', index, e.target.value)}
                            placeholder="https://youtube.com/..."
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor={`rumble_link_${index}`}>Rumble Link</label>
                          <input
                            id={`rumble_link_${index}`}
                            type="url"
                            value={formData.rumble_links[index] || ''}
                            onChange={(e) => handlePartLinkChange('rumble', index, e.target.value)}
                            placeholder="https://rumble.com/..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="form-group featured-toggle">
                <label className="featured-checkbox-label" htmlFor="featured">
                  <input
                    id="featured"
                    name="featured"
                    type="checkbox"
                    checked={Boolean(formData.featured)}
                    onChange={handleFeaturedChange}
                    disabled={!canSelectFeatured && !formData.featured}
                  />
                  <span>Featured book (shows first on the homepage)</span>
                </label>
                {!canSelectFeatured && (
                  <p className="featured-locked-hint">
                    "{currentFeatured?.name || 'Another book'}" is currently featured.
                    Edit that book and uncheck Featured before selecting a different one.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="cover_image">Cover Image</label>
                <input
                  id="cover_image"
                  name="cover_image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img 
                      src={imagePreview} 
                      alt="Cover preview" 
                      style={{ maxWidth: '200px', maxHeight: '300px', marginTop: '10px' }}
                    />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={closeForm}>
                  Cancel
                </button>
                <button type="submit" className="save-button" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Book'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;

